import { CxmMethod, CxmPayoutAccount, CxmPayoutAccountRequirements } from "src/api";

/**
 * The gateway's own answers, copied verbatim from the CXM test environment on
 * 2026-09-08 — the two rules granted to `shift_dev`, their limits, and the
 * payout schemas behind them. Nothing here is prettified: `max.amount: "0"`
 * really does mean "no ceiling", and the holder-name field really does end in
 * a stray `))`, which is why the forms carry code for both.
 */
export const DEPOSIT_METHODS: CxmMethod[] = [
  {
    methodId: "1",
    displayName: "Manual Test PSP",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "1", currency: "USD" }, max: { amount: "0", currency: "USD" } },
  },
  {
    methodId: "4",
    displayName: "LD PSP",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "0", currency: "USD" }, max: { amount: "0", currency: "USD" } },
  },
];

export const WITHDRAWAL_METHODS: CxmMethod[] = [
  {
    methodId: "1",
    displayName: "Manual Test PSP",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "1", currency: "USD" }, max: { amount: "0", currency: "USD" } },
    paymentMethodTypeId: 18,
  },
  {
    methodId: "4",
    displayName: "LD PSP",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "0", currency: "USD" }, max: { amount: "1000", currency: "USD" } },
    paymentMethodTypeId: 17,
  },
  {
    methodId: "5",
    displayName: "Bank Transfer (Gateway Test)",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "1", currency: "USD" }, max: { amount: "0", currency: "USD" } },
    paymentMethodTypeId: 45,
  },
  {
    methodId: "6",
    displayName: "PIX Brazil (Gateway Test)",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "1", currency: "USD" }, max: { amount: "0", currency: "USD" } },
    paymentMethodTypeId: 49,
  },
  {
    methodId: "7",
    displayName: "SWIFT Wire (Gateway Test)",
    deliveryMode: "none",
    currency: "USD",
    limits: { min: { amount: "1", currency: "USD" }, max: { amount: "0", currency: "USD" } },
    paymentMethodTypeId: 41,
  },
];

const TEXT_REQUIREMENTS: Record<number, CxmPayoutAccountRequirements> = {
  17: {
    paymentMethodTypeId: 17,
    type: "AccountNumber",
    fields: [
      {
        name: "Bank Account Holder Name (has to be the same person as CXM Direct account name))",
        hidden: false,
      },
      { name: "Bank Account Number", hidden: false },
      { name: "Recipient Bank Name", hidden: false },
    ],
  },
  18: {
    paymentMethodTypeId: 18,
    type: "Usdt",
    fields: [{ name: "USDT (Tether) ERC-20 Wallet Address", hidden: false }],
  },
};

/**
 * One saved account, on the USDT rail only. The bank rail is left empty on
 * purpose: that is the branch where adding an account IS the task, and it is
 * the screen that changed most, so the demo has to be able to reach it.
 */
export const SEED_ACCOUNTS: CxmPayoutAccount[] = [
  {
    id: 2,
    paymentMethodTypeId: 18,
    type: "Usdt",
    status: "approved",
    details: { "USDT (Tether) ERC-20 Wallet Address": "0x1234567890abcdef1234567890abcdef12345678" },
    createdAt: "2026-09-10T02:54:30.053995Z",
  },
];

/** Round-trips the real gateway showed us, so the pending states last as long. */
export const LATENCY = {
  catalogue: 750,
  accounts: 550,
  schema: 700,
  createAccount: 900,
  submit: 1200,
};

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * The three schemas granted on 2026-09-10, read from the gateway that day.
 * These are the ones with selects: 45 picks a bank from a list chosen by
 * country, 49 a PIX key type the same way, and 41 is plain text — kept so the
 * text-only branch is still reachable.
 *
 * 45's country list is cut from 20 to five here, banks intact; Nigeria's 274
 * is the longest the gateway returns, so the scrolling list is honestly
 * represented without carrying 54KB of fixture.
 */
const COMPLEX_REQUIREMENTS: Record<number, CxmPayoutAccountRequirements> = {
  45: {
      "paymentMethodTypeId": 45,
      "type": "BankTransferWithoutAddress",
      "fields": [
          {
              "name": "Account opening number",
              "kind": "text",
              "required": true,
              "hidden": false
          },
          {
              "name": "Country Code",
              "kind": "select",
              "required": true,
              "hidden": false,
              "options": [
                  {
                      "value": "BD",
                      "label": "Bangladesh"
                  },
                  {
                      "value": "HK",
                      "label": "HK"
                  },
                  {
                      "value": "ID",
                      "label": "Indonesia"
                  },
                  {
                      "value": "NG",
                      "label": "Nigeria"
                  },
                  {
                      "value": "BR",
                      "label": "Brazil"
                  }
              ],
              "source": "profile"
          },
          {
              "name": "Bank Code",
              "key": "bankCode",
              "kind": "select",
              "required": true,
              "hidden": false,
              "dependsOn": "Country Code",
              "options": {
                  "BD": [
                      {
                          "value": "BKASH",
                          "label": "bKash"
                      },
                      {
                          "value": "NAGAD",
                          "label": "Nagad"
                      },
                      {
                          "value": "ROCKET",
                          "label": "Rocket"
                      }
                  ],
                  "HK": [
                      {
                          "value": "395",
                          "label": "AIRSTAR BANK"
                      },
                      {
                          "value": "393",
                          "label": "ANT BANK"
                      },
                      {
                          "value": "12",
                          "label": "BANK OF CHINA"
                      },
                      {
                          "value": "27",
                          "label": "BANK OF COMMUNICATIONS"
                      },
                      {
                          "value": "18",
                          "label": "CHINA CITIC BANK"
                      },
                      {
                          "value": "9",
                          "label": "CHINA CONSTRUCTION BANK"
                      },
                      {
                          "value": "39",
                          "label": "CHIYU BANKING"
                      },
                      {
                          "value": "41",
                          "label": "CHONG HING BANK"
                      },
                      {
                          "value": "6",
                          "label": "CITIBANK"
                      },
                      {
                          "value": "20",
                          "label": "CMB WING LUNG BANK"
                      },
                      {
                          "value": "40",
                          "label": "DAH SING BANK"
                      },
                      {
                          "value": "16",
                          "label": "DBS BANK"
                      },
                      {
                          "value": "382",
                          "label": "Faster Payment System"
                      },
                      {
                          "value": "128",
                          "label": "FUBON BANK"
                      },
                      {
                          "value": "391",
                          "label": "FUSION BANK"
                      },
                      {
                          "value": "24",
                          "label": "HANG SENG BANK"
                      },
                      {
                          "value": "1201",
                          "label": "INDUSTRIAL AND COMMERCIAL BANK OF CHINA"
                      },
                      {
                          "value": "388",
                          "label": "LIVI BANK"
                      },
                      {
                          "value": "389",
                          "label": "MOX BANK"
                      },
                      {
                          "value": "43",
                          "label": "NANYANG COMMERCIAL BANK"
                      },
                      {
                          "value": "35",
                          "label": "OCBC WING HANG BANK"
                      },
                      {
                          "value": "392",
                          "label": "PING AN ONECONNECT BANK"
                      },
                      {
                          "value": "28",
                          "label": "PUBLIC BANK"
                      },
                      {
                          "value": "25",
                          "label": "SHANGHAI COMMERCIAL"
                      },
                      {
                          "value": "3",
                          "label": "STANDARD CHARTERED BANK"
                      },
                      {
                          "value": "703",
                          "label": "TAI YAU BANK"
                      },
                      {
                          "value": "712",
                          "label": "THE BANK OF EAST ASIA"
                      },
                      {
                          "value": "978",
                          "label": "THE HONGKONG AND SHANGHAI BANKING"
                      },
                      {
                          "value": "390",
                          "label": "WELAB BANK"
                      },
                      {
                          "value": "387",
                          "label": "ZA BANK"
                      }
                  ],
                  "ID": [
                      {
                          "value": "567",
                          "label": "Allo Bank/Bank Harda Internasional"
                      },
                      {
                          "value": "30",
                          "label": "American Express Bank"
                      },
                      {
                          "value": "531",
                          "label": "Anglomas International Bank"
                      },
                      {
                          "value": "987",
                          "label": "ARTAJASA PEMBAYARAN ELEKTRONIK"
                      },
                      {
                          "value": "40",
                          "label": "Bangkok Bank"
                      },
                      {
                          "value": "52",
                          "label": "Bank ABN Amro"
                      },
                      {
                          "value": "116",
                          "label": "Bank Aceh Syariah"
                      },
                      {
                          "value": "899",
                          "label": "Bank Agris"
                      },
                      {
                          "value": "187",
                          "label": "Bank Agris UUS"
                      },
                      {
                          "value": "494",
                          "label": "Bank Agroniaga"
                      },
                      {
                          "value": "192",
                          "label": "BANK AMAR INDONESIA"
                      },
                      {
                          "value": "466",
                          "label": "Bank Andara"
                      },
                      {
                          "value": "252",
                          "label": "Bank Antar Daerah"
                      },
                      {
                          "value": "61",
                          "label": "Bank ANZ Indonesia"
                      },
                      {
                          "value": "19",
                          "label": "Bank ANZ PANIN"
                      },
                      {
                          "value": "20",
                          "label": "Bank Arta Niaga Kencana"
                      },
                      {
                          "value": "37",
                          "label": "Bank Artha Graha International"
                      },
                      {
                          "value": "709",
                          "label": "Bank Artos Indonesia"
                      },
                      {
                          "value": "542",
                          "label": "Bank ARTOS/ Bank Jago"
                      },
                      {
                          "value": "525",
                          "label": "BANK BARCLAYS INDONESIA"
                      },
                      {
                          "value": "133",
                          "label": "Bank Bengkulu"
                      },
                      {
                          "value": "459",
                          "label": "Bank Bisnis Internasional"
                      },
                      {
                          "value": "110",
                          "label": "Bank BJB"
                      },
                      {
                          "value": "11001",
                          "label": "Bank BJB Syariah"
                      },
                      {
                          "value": "9",
                          "label": "Bank BNI Syariah"
                      },
                      {
                          "value": "57",
                          "label": "Bank BNP Paribas"
                      },
                      {
                          "value": "2",
                          "label": "Bank BRI Syariah"
                      },
                      {
                          "value": "213",
                          "label": "Bank BTPN"
                      },
                      {
                          "value": "21301",
                          "label": "Bank BTPN Syariah"
                      },
                      {
                          "value": "441",
                          "label": "Bank Bukopin"
                      },
                      {
                          "value": "44101",
                          "label": "Bank Bukopin Syariah"
                      },
                      {
                          "value": "76",
                          "label": "Bank Bumi Arta"
                      },
                      {
                          "value": "330",
                          "label": "BANK BUMIPUTERA"
                      },
                      {
                          "value": "54",
                          "label": "Bank Capital Indonesia"
                      },
                      {
                          "value": "14",
                          "label": "Bank Central Asia"
                      },
                      {
                          "value": "1401",
                          "label": "Bank Central Asia (BCA) Syariah"
                      },
                      {
                          "value": "469",
                          "label": "BANK CENTRATAMA"
                      },
                      {
                          "value": "36",
                          "label": "BANK CHINA CONSTRUCTION"
                      },
                      {
                          "value": "3601",
                          "label": "Bank China Construction Bank Indonesia"
                      },
                      {
                          "value": "424",
                          "label": "Bank Chinatrust Indonesia"
                      },
                      {
                          "value": "22",
                          "label": "Bank CIMB Niaga REKENING PONSEL"
                      },
                      {
                          "value": "2201",
                          "label": "Bank CIMB Niaga UUS"
                      },
                      {
                          "value": "950",
                          "label": "Bank Commonwealth"
                      },
                      {
                          "value": "39",
                          "label": "Bank Credit Agricole Indosuez"
                      },
                      {
                          "value": "11",
                          "label": "Bank Danamon"
                      },
                      {
                          "value": "1101",
                          "label": "Bank Danamon UUS"
                      },
                      {
                          "value": "46",
                          "label": "Bank DBS Indonesia"
                      },
                      {
                          "value": "526",
                          "label": "Bank Dinar Indonesia"
                      },
                      {
                          "value": "952",
                          "label": "BANK DIPO INTERNATIONAL"
                      },
                      {
                          "value": "111",
                          "label": "Bank DKI"
                      },
                      {
                          "value": "11101",
                          "label": "Bank DKI UUS"
                      },
                      {
                          "value": "699",
                          "label": "Bank EKA"
                      },
                      {
                          "value": "87",
                          "label": "BANK EKONOMI RAHARJA"
                      },
                      {
                          "value": "3",
                          "label": "Bank Ekspor Indonesia"
                      },
                      {
                          "value": "562",
                          "label": "Bank Fama International"
                      },
                      {
                          "value": "161",
                          "label": "Bank Ganesha"
                      },
                      {
                          "value": "160",
                          "label": "Bank Hana"
                      },
                      {
                          "value": "56701",
                          "label": "Bank Harda Internasional"
                      },
                      {
                          "value": "166",
                          "label": "Bank Harmoni International"
                      },
                      {
                          "value": "727",
                          "label": "Bank Himpunan Saudara 1906"
                      },
                      {
                          "value": "945",
                          "label": "Bank IBK Indonesia"
                      },
                      {
                          "value": "164",
                          "label": "Bank ICBC Indonesia"
                      },
                      {
                          "value": "513",
                          "label": "Bank Ina Perdana"
                      },
                      {
                          "value": "555",
                          "label": "Bank Index Selindo"
                      },
                      {
                          "value": "54201",
                          "label": "BANK JAGO"
                      },
                      {
                          "value": "115",
                          "label": "Bank Jambi"
                      },
                      {
                          "value": "472",
                          "label": "Bank Jasa Jakarta"
                      },
                      {
                          "value": "113",
                          "label": "Bank Jateng"
                      },
                      {
                          "value": "11301",
                          "label": "Bank Jateng Syariah"
                      },
                      {
                          "value": "11302",
                          "label": "Bank Jateng Syariah"
                      },
                      {
                          "value": "114",
                          "label": "Bank Jatim"
                      },
                      {
                          "value": "11401",
                          "label": "Bank Jatim UUS"
                      },
                      {
                          "value": "11002",
                          "label": "Bank Jawa Barat"
                      },
                      {
                          "value": "95",
                          "label": "Bank JTrust Indonesia"
                      },
                      {
                          "value": "53",
                          "label": "Bank Keppel Tatlee Buana"
                      },
                      {
                          "value": "182",
                          "label": "Bank Kesejahteraan Ekonomi"
                      },
                      {
                          "value": "131",
                          "label": "Bank Maluku"
                      },
                      {
                          "value": "8",
                          "label": "Bank Mandiri"
                      },
                      {
                          "value": "801",
                          "label": "Bank Mandiri Taspen Pos"
                      },
                      {
                          "value": "564",
                          "label": "Bank MANTAP"
                      },
                      {
                          "value": "157",
                          "label": "Bank Maspion Indonesia"
                      },
                      {
                          "value": "97",
                          "label": "Bank Mayapada"
                      },
                      {
                          "value": "9701",
                          "label": "Bank Mayapada International"
                      },
                      {
                          "value": "16",
                          "label": "Bank Maybank"
                      },
                      {
                          "value": "451",
                          "label": "Bank Maybank Syariah Indonesia"
                      },
                      {
                          "value": "45101",
                          "label": "Bank Maybank Syariah Indonesia UUS"
                      },
                      {
                          "value": "553",
                          "label": "Bank Mayora"
                      },
                      {
                          "value": "426",
                          "label": "Bank Mega"
                      },
                      {
                          "value": "506",
                          "label": "Bank Mega Syariah"
                      },
                      {
                          "value": "151",
                          "label": "Bank Mestika Dharma"
                      },
                      {
                          "value": "698",
                          "label": "Bank Metro Express"
                      },
                      {
                          "value": "491",
                          "label": "Bank Mitra Niaga"
                      },
                      {
                          "value": "48",
                          "label": "Bank Mizuho Indonesia"
                      },
                      {
                          "value": "485",
                          "label": "Bank MNC Internasional"
                      },
                      {
                          "value": "147",
                          "label": "Bank Muamalat Indonesia"
                      },
                      {
                          "value": "548",
                          "label": "Bank Multi Arta Sentosa"
                      },
                      {
                          "value": "630",
                          "label": "Bank MULTICOR"
                      },
                      {
                          "value": "321",
                          "label": "Bank Mutiara"
                      },
                      {
                          "value": "118",
                          "label": "BANK NAGARI"
                      },
                      {
                          "value": "503",
                          "label": "Bank Nationalnobu"
                      },
                      {
                          "value": "901",
                          "label": "Bank Negara Indonesia"
                      },
                      {
                          "value": "583",
                          "label": "BANK NIAGA TBK. SYARIAH"
                      },
                      {
                          "value": "145",
                          "label": "Bank Nusantara Parahyangan"
                      },
                      {
                          "value": "28",
                          "label": "Bank OCBC NISP"
                      },
                      {
                          "value": "2801",
                          "label": "Bank OCBC NISP UUS"
                      },
                      {
                          "value": "60",
                          "label": "Bank of America Merill-Lynch"
                      },
                      {
                          "value": "6001",
                          "label": "BANK OF AMERICA NA"
                      },
                      {
                          "value": "69",
                          "label": "Bank of China"
                      },
                      {
                          "value": "146",
                          "label": "Bank of India Indonesia"
                      },
                      {
                          "value": "42",
                          "label": "Bank of Tokyo"
                      },
                      {
                          "value": "607",
                          "label": "Bank of Tokyo Mitsubishi UFJ"
                      },
                      {
                          "value": "775",
                          "label": "Bank Oke Indonesia"
                      },
                      {
                          "value": "1901",
                          "label": "Bank Panin"
                      },
                      {
                          "value": "1902",
                          "label": "Bank Panin Syariah"
                      },
                      {
                          "value": "132",
                          "label": "Bank Papua"
                      },
                      {
                          "value": "117",
                          "label": "Bank Pembangunan Daerah (BPD DIY)"
                      },
                      {
                          "value": "367",
                          "label": "Bank Pembangunan Daerah (BPD DIY) Syariah"
                      },
                      {
                          "value": "162",
                          "label": "BANK PEMBANGUNAN DAERAH DIY UNIT USAHA SYARIAH"
                      },
                      {
                          "value": "13",
                          "label": "Bank Permata"
                      },
                      {
                          "value": "1301",
                          "label": "Bank Permata UUS"
                      },
                      {
                          "value": "584",
                          "label": "Bank Pundi Indonesia"
                      },
                      {
                          "value": "820",
                          "label": "BANK PURBA DANARTA"
                      },
                      {
                          "value": "167",
                          "label": "Bank QNB Kesawan"
                      },
                      {
                          "value": "6002",
                          "label": "Bank Rabobank International Indonesia"
                      },
                      {
                          "value": "201",
                          "label": "Bank Rakyat Indonesia"
                      },
                      {
                          "value": "47",
                          "label": "Bank Resona Perdania"
                      },
                      {
                          "value": "959",
                          "label": "Bank Royal Indonesia"
                      },
                      {
                          "value": "82001",
                          "label": "Bank Sahabat Purba Danarta"
                      },
                      {
                          "value": "523",
                          "label": "Bank Sahabat Sampoerna"
                      },
                      {
                          "value": "7601",
                          "label": "Bank SBI Indonesia"
                      },
                      {
                          "value": "152",
                          "label": "Bank Shinhan Indonesia"
                      },
                      {
                          "value": "129",
                          "label": "Bank Sinar Harapan Bali"
                      },
                      {
                          "value": "153",
                          "label": "Bank Sinarmas Syariah"
                      },
                      {
                          "value": "15301",
                          "label": "Bank Sinarmas UUS"
                      },
                      {
                          "value": "126",
                          "label": "Bank Sulselbar"
                      },
                      {
                          "value": "12601",
                          "label": "Bank Sulselbar UUS"
                      },
                      {
                          "value": "134",
                          "label": "Bank Sulteng"
                      },
                      {
                          "value": "135",
                          "label": "Bank Sultra"
                      },
                      {
                          "value": "45",
                          "label": "Bank Sumitomo Mitsui Indonesia"
                      },
                      {
                          "value": "120",
                          "label": "Bank Sumsel Babel"
                      },
                      {
                          "value": "12001",
                          "label": "Bank Sumsel Dan Babel UUS"
                      },
                      {
                          "value": "11701",
                          "label": "Bank Sumut"
                      },
                      {
                          "value": "11702",
                          "label": "Bank Sumut UUS"
                      },
                      {
                          "value": "202",
                          "label": "Bank Syariah BRI"
                      },
                      {
                          "value": "44102",
                          "label": "Bank Syariah Bukopin"
                      },
                      {
                          "value": "45102",
                          "label": "Bank Syariah Indonesia"
                      },
                      {
                          "value": "802",
                          "label": "Bank Syariah Mandiri"
                      },
                      {
                          "value": "245",
                          "label": "Bank Syariah Mega"
                      },
                      {
                          "value": "200",
                          "label": "Bank Tabungan Negara"
                      },
                      {
                          "value": "20001",
                          "label": "Bank Tabungan Negara (BTN) UUS"
                      },
                      {
                          "value": "21302",
                          "label": "Bank Tabungan Pensiunan Nasional"
                      },
                      {
                          "value": "23",
                          "label": "Bank UOB Indonesia"
                      },
                      {
                          "value": "566",
                          "label": "Bank Victoria Internasional"
                      },
                      {
                          "value": "56601",
                          "label": "Bank Victoria Syariah"
                      },
                      {
                          "value": "821",
                          "label": "Bank Windu Kentjana Int"
                      },
                      {
                          "value": "244",
                          "label": "Bank Woori Indonesia"
                      },
                      {
                          "value": "212",
                          "label": "Bank Woori Saudara"
                      },
                      {
                          "value": "209",
                          "label": "Bank Yudha Bhakti"
                      },
                      {
                          "value": "1402",
                          "label": "Blu/BCA Digital"
                      },
                      {
                          "value": "5701",
                          "label": "BNP Paribas Indonesia"
                      },
                      {
                          "value": "11601",
                          "label": "BPD Aceh"
                      },
                      {
                          "value": "12901",
                          "label": "BPD Bali"
                      },
                      {
                          "value": "558",
                          "label": "BPD Banten"
                      },
                      {
                          "value": "13301",
                          "label": "BPD Bengkulu"
                      },
                      {
                          "value": "112",
                          "label": "BPD DIY"
                      },
                      {
                          "value": "11602",
                          "label": "BPD ISTIMEWA ACEH SYARIAH"
                      },
                      {
                          "value": "11501",
                          "label": "BPD Jambi"
                      },
                      {
                          "value": "747",
                          "label": "BPD Jawa Tengah"
                      },
                      {
                          "value": "266",
                          "label": "BPD JAWA TENGAH UNIT USAHA SYARIAH"
                      },
                      {
                          "value": "951",
                          "label": "BPD Jawa Tengah UUS"
                      },
                      {
                          "value": "215",
                          "label": "BPD Jawa Timur"
                      },
                      {
                          "value": "356",
                          "label": "BPD Jawa Timur UUS"
                      },
                      {
                          "value": "305",
                          "label": "BPD Kalimantan Barat"
                      },
                      {
                          "value": "733",
                          "label": "BPD Kalimantan Barat UUS"
                      },
                      {
                          "value": "123",
                          "label": "BPD Kalimantan Barat/Kalbar"
                      },
                      {
                          "value": "600",
                          "label": "BPD Kalimantan Selatan"
                      },
                      {
                          "value": "661",
                          "label": "BPD Kalimantan Selatan UUS"
                      },
                      {
                          "value": "122",
                          "label": "BPD Kalimantan Selatan/Kalsel"
                      },
                      {
                          "value": "125",
                          "label": "BPD Kalimantan Tengah"
                      },
                      {
                          "value": "124",
                          "label": "BPD Kalimantan Timur"
                      },
                      {
                          "value": "928",
                          "label": "BPD Kalimantan Timur UUS"
                      },
                      {
                          "value": "121",
                          "label": "BPD Lampung"
                      },
                      {
                          "value": "13101",
                          "label": "BPD Maluku"
                      },
                      {
                          "value": "688",
                          "label": "BPD Nusa Tenggara Barat"
                      },
                      {
                          "value": "128",
                          "label": "BPD Nusa Tenggara Barat(NTB)"
                      },
                      {
                          "value": "130",
                          "label": "BPD Nusa Tenggara Timur"
                      },
                      {
                          "value": "13201",
                          "label": "BPD Papua"
                      },
                      {
                          "value": "119",
                          "label": "BPD Riau Dan Kepri"
                      },
                      {
                          "value": "11901",
                          "label": "BPD Riau Dan Kepri UUS"
                      },
                      {
                          "value": "601",
                          "label": "BPD Sulawesi Tengah"
                      },
                      {
                          "value": "679",
                          "label": "BPD Sulawesi Tenggara"
                      },
                      {
                          "value": "127",
                          "label": "BPD Sulawesi Utara(SulutGo)"
                      },
                      {
                          "value": "12602",
                          "label": "BPD Sulselbar"
                      },
                      {
                          "value": "12603",
                          "label": "BPD Sulselbar UUS"
                      },
                      {
                          "value": "12701",
                          "label": "BPD Sulut"
                      },
                      {
                          "value": "11801",
                          "label": "BPD Sumatera Barat"
                      },
                      {
                          "value": "99",
                          "label": "BPD Sumatera Barat UUS"
                      },
                      {
                          "value": "12002",
                          "label": "BPD Sumsel Babel"
                      },
                      {
                          "value": "12003",
                          "label": "BPD Sumsel Dan Babel"
                      },
                      {
                          "value": "11703",
                          "label": "BPD Sumut"
                      },
                      {
                          "value": "11704",
                          "label": "BPD Sumut UUS"
                      },
                      {
                          "value": "909",
                          "label": "BPD_Daerah_Istimewa_Yogyakarta_"
                      },
                      {
                          "value": "867",
                          "label": "BPR EKA (Bank Eka)"
                      },
                      {
                          "value": "68801",
                          "label": "BPR KS"
                      },
                      {
                          "value": "21303",
                          "label": "BTPN Syariah"
                      },
                      {
                          "value": "3602",
                          "label": "CCB Indonesia"
                      },
                      {
                          "value": "559",
                          "label": "Centratama Nasional Bank"
                      },
                      {
                          "value": "2202",
                          "label": "CIMB Niaga Bank"
                      },
                      {
                          "value": "31",
                          "label": "Citibank"
                      },
                      {
                          "value": "949",
                          "label": "CTBC Indonesia"
                      },
                      {
                          "value": "82002",
                          "label": "DANA"
                      },
                      {
                          "value": "67",
                          "label": "Deutsche Bank"
                      },
                      {
                          "value": "82003",
                          "label": "GOPAY"
                      },
                      {
                          "value": "771",
                          "label": "Hongkong and Shanghai Bank"
                      },
                      {
                          "value": "41",
                          "label": "HSBC"
                      },
                      {
                          "value": "34",
                          "label": "ING Indonesia Bank"
                      },
                      {
                          "value": "32",
                          "label": "JP Morgan Chase Bank"
                      },
                      {
                          "value": "1102",
                          "label": "Korea Exchange Bank Danamon"
                      },
                      {
                          "value": "484",
                          "label": "LINE Bank/KEB Hana"
                      },
                      {
                          "value": "911",
                          "label": "LINKAJA"
                      },
                      {
                          "value": "48501",
                          "label": "Motion/Bank MNC Internasional"
                      },
                      {
                          "value": "490",
                          "label": "Neo Commerce/Bank Yudha Bhakti"
                      },
                      {
                          "value": "810",
                          "label": "OVO"
                      },
                      {
                          "value": "1903",
                          "label": "Panin Dubai Syariah"
                      },
                      {
                          "value": "520",
                          "label": "Prima Master Bank"
                      },
                      {
                          "value": "871",
                          "label": "QNB Indonesia"
                      },
                      {
                          "value": "207",
                          "label": "QRIS"
                      },
                      {
                          "value": "89",
                          "label": "Rabobank International Indonesia"
                      },
                      {
                          "value": "501",
                          "label": "Royal Bank of Scotland"
                      },
                      {
                          "value": "535",
                          "label": "Seabank/Bank Kesejahteraan Ekonomi"
                      },
                      {
                          "value": "898",
                          "label": "SHOPEEPAY"
                      },
                      {
                          "value": "15302",
                          "label": "Sinarmas"
                      },
                      {
                          "value": "50",
                          "label": "Standard Charted Bank"
                      },
                      {
                          "value": "2301",
                          "label": "TMRW/Bank UOB Indonesia"
                      },
                      {
                          "value": "44103",
                          "label": "Wokee/Bukopin"
                      }
                  ],
                  "NG": [
                      {
                          "value": "120001",
                          "label": "9 Payment Service Bank"
                      },
                      {
                          "value": "090270",
                          "label": "AB Microfinance Bank"
                      },
                      {
                          "value": "070010",
                          "label": "Abbey Mortgage Bank"
                      },
                      {
                          "value": "090260",
                          "label": "Above Only Microfinance Bank"
                      },
                      {
                          "value": "090197",
                          "label": "ABU Microfinance Bank"
                      },
                      {
                          "value": "044",
                          "label": "Access Bank Nigeria"
                      },
                      {
                          "value": "063",
                          "label": "Access Bank Plc (Diamond)"
                      },
                      {
                          "value": "323",
                          "label": "ACCESS MOBILE"
                      },
                      {
                          "value": "927",
                          "label": "Access Money"
                      },
                      {
                          "value": "100052",
                          "label": "Access Yello & Beta"
                      },
                      {
                          "value": "090134",
                          "label": "Accion Microfinance Bank"
                      },
                      {
                          "value": "090160",
                          "label": "Addosser Microfinance Bank"
                      },
                      {
                          "value": "090268",
                          "label": "Adeyemi College Staff Microfinance Bank"
                      },
                      {
                          "value": "100028",
                          "label": "AG Mortgage Bank"
                      },
                      {
                          "value": "090133",
                          "label": "AL-Barakah Microfinance Bank"
                      },
                      {
                          "value": "090277",
                          "label": "Al-Hayat Microfinance Bank"
                      },
                      {
                          "value": "090259",
                          "label": "Alekun Microfinance Bank"
                      },
                      {
                          "value": "090297",
                          "label": "Alert MFB"
                      },
                      {
                          "value": "090131",
                          "label": "Allworkers Microfinance Bank"
                      },
                      {
                          "value": "090169",
                          "label": "Alpha Kapital Microfinance Bank"
                      },
                      {
                          "value": "000037",
                          "label": "Alternative Bank"
                      },
                      {
                          "value": "090180",
                          "label": "AMJU Unique Microfinance Bank"
                      },
                      {
                          "value": "914",
                          "label": "AMML Micro-finance Bank"
                      },
                      {
                          "value": "090143",
                          "label": "Apeks Microfinance Bank"
                      },
                      {
                          "value": "090282",
                          "label": "Arise MFB"
                      },
                      {
                          "value": "401",
                          "label": "Aso Savings and Loans"
                      },
                      {
                          "value": "090172",
                          "label": "Astrapolaris Microfinance Bank"
                      },
                      {
                          "value": "090264",
                          "label": "Auchi Microfinance Bank"
                      },
                      {
                          "value": "090188",
                          "label": "Baines Credit Microfinance Bank"
                      },
                      {
                          "value": "090326",
                          "label": "Balogun Gambari MFB"
                      },
                      {
                          "value": "090316",
                          "label": "Bayero MICROFINANCE BANK"
                      },
                      {
                          "value": "090127",
                          "label": "BC Kash Microfinance Bank"
                      },
                      {
                          "value": "090336",
                          "label": "BIPC MICROFINANCE BANK"
                      },
                      {
                          "value": "952",
                          "label": "BOCTrust Micro-finance Bank"
                      },
                      {
                          "value": "090176",
                          "label": "Bosak Microfinance Bank"
                      },
                      {
                          "value": "090148",
                          "label": "Bowen Microfinance Bank"
                      },
                      {
                          "value": "050006",
                          "label": "Branch International Financial Services"
                      },
                      {
                          "value": "070015",
                          "label": "Brent Mortgage Bank"
                      },
                      {
                          "value": "090293",
                          "label": "BRETHREN MICROFINANCE BANK"
                      },
                      {
                          "value": "090393",
                          "label": "BRIDGEWAY MICROFINANCE BANK"
                      },
                      {
                          "value": "090308",
                          "label": "Brightway MFB"
                      },
                      {
                          "value": "940",
                          "label": "Carbon (One Finance)"
                      },
                      {
                          "value": "919",
                          "label": "Cellulant"
                      },
                      {
                          "value": "090154",
                          "label": "CEMCS Microfinance Bank"
                      },
                      {
                          "value": "929",
                          "label": "Chams Mobile"
                      },
                      {
                          "value": "090141",
                          "label": "Chikum Microfinance Bank"
                      },
                      {
                          "value": "090144",
                          "label": "CIT Microfinance Bank"
                      },
                      {
                          "value": "023",
                          "label": "Citi Bank"
                      },
                      {
                          "value": "090130",
                          "label": "Consumer Microfinance Bank"
                      },
                      {
                          "value": "100032",
                          "label": "Contec Global Infotech Limited (NowNow)"
                      },
                      {
                          "value": "559",
                          "label": "Coronation Merchant Bank"
                      },
                      {
                          "value": "949",
                          "label": "Covenant Micro-finance Bank"
                      },
                      {
                          "value": "090159",
                          "label": "Credit Afrique Microfinance Bank"
                      },
                      {
                          "value": "090167",
                          "label": "Daylight Microfinance Bank"
                      },
                      {
                          "value": "529",
                          "label": "DOT Microfinance Bank"
                      },
                      {
                          "value": "090156",
                          "label": "e-Barcs Microfinance Bank"
                      },
                      {
                          "value": "090294",
                          "label": "Eagle Flight MFB"
                      },
                      {
                          "value": "935",
                          "label": "Eartholeum"
                      },
                      {
                          "value": "050",
                          "label": "Ecobank"
                      },
                      {
                          "value": "922",
                          "label": "Ecobank Express Account"
                      },
                      {
                          "value": "307",
                          "label": "Ecobank Mobile"
                      },
                      {
                          "value": "090310",
                          "label": "Edfin MFB"
                      },
                      {
                          "value": "090097",
                          "label": "Ekondo MFB"
                      },
                      {
                          "value": "090273",
                          "label": "Emeralds Microfinance Bank"
                      },
                      {
                          "value": "913",
                          "label": "Empire Trust Micro-finance Bank"
                      },
                      {
                          "value": "084",
                          "label": "Enterprise Bank"
                      },
                      {
                          "value": "090189",
                          "label": "Esan Microfinance Bank"
                      },
                      {
                          "value": "090166",
                          "label": "Eso-E Microfinance Bank"
                      },
                      {
                          "value": "090304",
                          "label": "Evangel MFB"
                      },
                      {
                          "value": "090332",
                          "label": "Evergreen MICROFINANCE BANK"
                      },
                      {
                          "value": "090328",
                          "label": "Eyowo MFB"
                      },
                      {
                          "value": "090551",
                          "label": "FairMoney MFB"
                      },
                      {
                          "value": "090179",
                          "label": "FAST Microfinance Bank"
                      },
                      {
                          "value": "309",
                          "label": "FBN MOBILE"
                      },
                      {
                          "value": "090107",
                          "label": "FBN Mortgages"
                      },
                      {
                          "value": "911",
                          "label": "FBNQuest Merchant Bank"
                      },
                      {
                          "value": "214",
                          "label": "FCMB"
                      },
                      {
                          "value": "100031",
                          "label": "FCMB Easy Account"
                      },
                      {
                          "value": "090290",
                          "label": "FCT MFB"
                      },
                      {
                          "value": "090318",
                          "label": "FEDERAL UNIVERSITY DUTSE MICROFINANCE BANK"
                      },
                      {
                          "value": "090298",
                          "label": "FederalPoly NasarawaMFB"
                      },
                      {
                          "value": "915",
                          "label": "FETS"
                      },
                      {
                          "value": "090153",
                          "label": "FFS Microfinance Bank"
                      },
                      {
                          "value": "070",
                          "label": "Fidelity Bank"
                      },
                      {
                          "value": "933",
                          "label": "Fidelity Mobile"
                      },
                      {
                          "value": "090126",
                          "label": "Fidfund Microfinance Bank"
                      },
                      {
                          "value": "090111",
                          "label": "FINATRUST MICROFINANCE BANK"
                      },
                      {
                          "value": "090366",
                          "label": "Firmus MFB"
                      },
                      {
                          "value": "011",
                          "label": "First Bank of Nigeria"
                      },
                      {
                          "value": "070014",
                          "label": "First Generation Mortgage Bank"
                      },
                      {
                          "value": "090163",
                          "label": "First Multiple MFB"
                      },
                      {
                          "value": "090285",
                          "label": "First Option MFB"
                      },
                      {
                          "value": "090164",
                          "label": "First Royal Microfinance Bank"
                      },
                      {
                          "value": "910",
                          "label": "First Trust Mortgage Bank"
                      },
                      {
                          "value": "928",
                          "label": "FirstMonie Wallet"
                      },
                      {
                          "value": "110002",
                          "label": "Flutterwave Technology Solutions"
                      },
                      {
                          "value": "948",
                          "label": "Fortis Micro-finance Bank"
                      },
                      {
                          "value": "930",
                          "label": "Fortis Mobile"
                      },
                      {
                          "value": "601",
                          "label": "FSDH Merchant Bank"
                      },
                      {
                          "value": "090145",
                          "label": "Fullrange Microfinance Bank"
                      },
                      {
                          "value": "090158",
                          "label": "Futo Microfinance Bank"
                      },
                      {
                          "value": "090168",
                          "label": "Gashua Microfinance Bank"
                      },
                      {
                          "value": "070009",
                          "label": "Gateway Mortgage Bank"
                      },
                      {
                          "value": "103",
                          "label": "Globus Bank"
                      },
                      {
                          "value": "090278",
                          "label": "Glory MFB"
                      },
                      {
                          "value": "100022",
                          "label": "GoMoney"
                      },
                      {
                          "value": "090495",
                          "label": "GOODNEWS MICROFINANCE BANK"
                      },
                      {
                          "value": "090122",
                          "label": "Gowans Microfinance Bank"
                      },
                      {
                          "value": "090178",
                          "label": "GreenBank Microfinance Bank"
                      },
                      {
                          "value": "090269",
                          "label": "Greenville Microfinance Bank"
                      },
                      {
                          "value": "060004",
                          "label": "Greenwich Merchant Bank"
                      },
                      {
                          "value": "090195",
                          "label": "Grooming Microfinance Bank"
                      },
                      {
                          "value": "923",
                          "label": "GT MOBILE"
                      },
                      {
                          "value": "058",
                          "label": "GTBank"
                      },
                      {
                          "value": "315",
                          "label": "GTBank Mobile Money"
                      },
                      {
                          "value": "090147",
                          "label": "Hackman Microfinance Bank"
                      },
                      {
                          "value": "070017",
                          "label": "Haggai Mortgage Bank"
                      },
                      {
                          "value": "090291",
                          "label": "Hala MFB"
                      },
                      {
                          "value": "090539",
                          "label": "Halo Microfinance Bank"
                      },
                      {
                          "value": "958",
                          "label": "Hasal Micro-finance Bank"
                      },
                      {
                          "value": "931",
                          "label": "Hedonmark"
                      },
                      {
                          "value": "030",
                          "label": "Heritage Bank"
                      },
                      {
                          "value": "090118",
                          "label": "IBILE Microfinance Bank"
                      },
                      {
                          "value": "090324",
                          "label": "IKENNE MFB"
                      },
                      {
                          "value": "090279",
                          "label": "Ikire MFB"
                      },
                      {
                          "value": "090258",
                          "label": "Imo State Microfinance Bank"
                      },
                      {
                          "value": "938",
                          "label": "Imperial Homes Mortgage Bank"
                      },
                      {
                          "value": "090157",
                          "label": "Infinity Microfinance Bank"
                      },
                      {
                          "value": "070016",
                          "label": "Infinity Trust Mortgage Bank"
                      },
                      {
                          "value": "100029",
                          "label": "Innovectives Kesh"
                      },
                      {
                          "value": "941",
                          "label": "Intellfin"
                      },
                      {
                          "value": "090149",
                          "label": "IRL Microfinance Bank"
                      },
                      {
                          "value": "301",
                          "label": "JAIZ Bank"
                      },
                      {
                          "value": "906",
                          "label": "Jubliee Life"
                      },
                      {
                          "value": "110008",
                          "label": "Kadick Integration"
                      },
                      {
                          "value": "090320",
                          "label": "Kadpoly MICROFINANCE BANK"
                      },
                      {
                          "value": "090191",
                          "label": "KCMB Microfinance Bank"
                      },
                      {
                          "value": "100015",
                          "label": "Kegow"
                      },
                      {
                          "value": "082",
                          "label": "Keystone Bank"
                      },
                      {
                          "value": "090299",
                          "label": "Kontagora MFB"
                      },
                      {
                          "value": "50211",
                          "label": "Kuda Micro-finance Bank"
                      },
                      {
                          "value": "090155",
                          "label": "La Fayette Microfinance Bank"
                      },
                      {
                          "value": "070012",
                          "label": "Lagos Building Investment Company"
                      },
                      {
                          "value": "090177",
                          "label": "Lapo Microfinance Bank"
                      },
                      {
                          "value": "090271",
                          "label": "Lavender Microfinance Bank"
                      },
                      {
                          "value": "090420",
                          "label": "Letshego MFB"
                      },
                      {
                          "value": "303",
                          "label": "Lotus Bank"
                      },
                      {
                          "value": "090265",
                          "label": "Lovonus Microfinance Bank"
                      },
                      {
                          "value": "100035",
                          "label": "M36**"
                      },
                      {
                          "value": "090323",
                          "label": "Mainland MICROFINANCE BANK"
                      },
                      {
                          "value": "014",
                          "label": "Mainstreet Micro-finance Bank"
                      },
                      {
                          "value": "090174",
                          "label": "Malachy Microfinance Bank"
                      },
                      {
                          "value": "090383",
                          "label": "Manny Microfinance bank"
                      },
                      {
                          "value": "090423",
                          "label": "MAUTECH Microfinance Bank"
                      },
                      {
                          "value": "090321",
                          "label": "Mayfair MFB"
                      },
                      {
                          "value": "070019",
                          "label": "MayFresh Mortgage Bank"
                      },
                      {
                          "value": "090280",
                          "label": "Megapraise Microfinance Bank"
                      },
                      {
                          "value": "090275",
                          "label": "Meridian MFB"
                      },
                      {
                          "value": "090136",
                          "label": "Microcred Microfinance Bank"
                      },
                      {
                          "value": "090192",
                          "label": "Midland Microfinance Bank"
                      },
                      {
                          "value": "50304",
                          "label": "Mint-Finex Micro-finance Bank"
                      },
                      {
                          "value": "100011",
                          "label": "Mkudi"
                      },
                      {
                          "value": "120003",
                          "label": "Momo PSB"
                      },
                      {
                          "value": "963",
                          "label": "Money Trust Micro-finance Bank"
                      },
                      {
                          "value": "934",
                          "label": "MoneyBox"
                      },
                      {
                          "value": "090405",
                          "label": "Moniepoint Microfinance Bank"
                      },
                      {
                          "value": "090190",
                          "label": "Mutual Benefits Microfinance Bank"
                      },
                      {
                          "value": "090151",
                          "label": "Mutual Trust Microfinance Bank"
                      },
                      {
                          "value": "090152",
                          "label": "Nagarta Microfinance Bank"
                      },
                      {
                          "value": "090263",
                          "label": "Navy Microfinance Bank"
                      },
                      {
                          "value": "090128",
                          "label": "Ndiorah Microfinance Bank"
                      },
                      {
                          "value": "090205",
                          "label": "New Dawn Microfinance Bank"
                      },
                      {
                          "value": "090108",
                          "label": "New Prudential Bank"
                      },
                      {
                          "value": "999999",
                          "label": "NIP Virtual Bank"
                      },
                      {
                          "value": "090194",
                          "label": "NIRSAL Microfinance Bank"
                      },
                      {
                          "value": "090283",
                          "label": "Nnew women MFB"
                      },
                      {
                          "value": "110028",
                          "label": "Nombba"
                      },
                      {
                          "value": "637",
                          "label": "Nova Merchant Bank"
                      },
                      {
                          "value": "947",
                          "label": "NPF Micro-finance Bank"
                      },
                      {
                          "value": "090333",
                          "label": "Oche MFB"
                      },
                      {
                          "value": "090119",
                          "label": "Ohafia Microfinance Bank"
                      },
                      {
                          "value": "090161",
                          "label": "Okpoga Microfinance Bank"
                      },
                      {
                          "value": "090272",
                          "label": "Olabisi Onabanjo University Microfinance Bank"
                      },
                      {
                          "value": "090295",
                          "label": "Omiye MFB"
                      },
                      {
                          "value": "950",
                          "label": "Omoluabi Mortgage Bank"
                      },
                      {
                          "value": "305",
                          "label": "Opay"
                      },
                      {
                          "value": "000036",
                          "label": "Optimus Bank"
                      },
                      {
                          "value": "916",
                          "label": "PAGA"
                      },
                      {
                          "value": "951",
                          "label": "Page Micro-finance Bank"
                      },
                      {
                          "value": "100033",
                          "label": "PALMPAY"
                      },
                      {
                          "value": "526",
                          "label": "PARALLEX BANK"
                      },
                      {
                          "value": "311",
                          "label": "Parkway"
                      },
                      {
                          "value": "917",
                          "label": "ParkWay-ReadyCash"
                      },
                      {
                          "value": "090004",
                          "label": "Parralex Microfinance bank"
                      },
                      {
                          "value": "090317",
                          "label": "PatrickGold Microfinance Bank"
                      },
                      {
                          "value": "943",
                          "label": "PayAttitude Online"
                      },
                      {
                          "value": "090137",
                          "label": "PecanTrust Microfinance Bank"
                      },
                      {
                          "value": "090196",
                          "label": "Pennywise Microfinance Bank"
                      },
                      {
                          "value": "090135",
                          "label": "Personal Trust Microfinance Bank"
                      },
                      {
                          "value": "090165",
                          "label": "Petra Microfinance Bank"
                      },
                      {
                          "value": "090289",
                          "label": "Pillar MFB"
                      },
                      {
                          "value": "070013",
                          "label": "Platinum Mortgage Bank"
                      },
                      {
                          "value": "076",
                          "label": "Polaris Bank"
                      },
                      {
                          "value": "090296",
                          "label": "Polyuwanna MFB"
                      },
                      {
                          "value": "105",
                          "label": "Premium Trust Bank"
                      },
                      {
                          "value": "090274",
                          "label": "Prestige Microfinance bank"
                      },
                      {
                          "value": "101",
                          "label": "Providus Bank"
                      },
                      {
                          "value": "090303",
                          "label": "Purplemoney MFB"
                      },
                      {
                          "value": "090261",
                          "label": "Quickfund Microfinance Bank"
                      },
                      {
                          "value": "090170",
                          "label": "Rahama MFB"
                      },
                      {
                          "value": "502",
                          "label": "Rand Merchant Bank"
                      },
                      {
                          "value": "070011",
                          "label": "Refuge Mortgage Bank"
                      },
                      {
                          "value": "955",
                          "label": "Regent Micro-finance Bank"
                      },
                      {
                          "value": "090173",
                          "label": "Reliance Microfinance Bank"
                      },
                      {
                          "value": "090198",
                          "label": "RenMoney Microfinance Bank"
                      },
                      {
                          "value": "090322",
                          "label": "Rephidim MICROFINANCE BANK"
                      },
                      {
                          "value": "090132",
                          "label": "Richway Microfinance Bank"
                      },
                      {
                          "value": "090138",
                          "label": "Royal Exchange Microfinance Bank"
                      },
                      {
                          "value": "125",
                          "label": "Rubies Micro-finance Bank"
                      },
                      {
                          "value": "090286",
                          "label": "Safe Haven MFB"
                      },
                      {
                          "value": "909",
                          "label": "SafeTrust"
                      },
                      {
                          "value": "966",
                          "label": "Sagamu Micro-finance Bank"
                      },
                      {
                          "value": "609",
                          "label": "Seed Capital Micro-finance Bank"
                      },
                      {
                          "value": "000034",
                          "label": "Signature Bank"
                      },
                      {
                          "value": "120004",
                          "label": "Smartcash Payment Service Bank"
                      },
                      {
                          "value": "090325",
                          "label": "Sparkle"
                      },
                      {
                          "value": "921",
                          "label": "Stanbic IBTC @ease Wallet"
                      },
                      {
                          "value": "221",
                          "label": "Stanbic IBTC Bank"
                      },
                      {
                          "value": "304",
                          "label": "Stanbic Mobile"
                      },
                      {
                          "value": "068",
                          "label": "Standard Chartered Bank"
                      },
                      {
                          "value": "090162",
                          "label": "Stanford Microfinance Bank"
                      },
                      {
                          "value": "090262",
                          "label": "Stellas Microfinance Bank"
                      },
                      {
                          "value": "232",
                          "label": "Sterling Bank"
                      },
                      {
                          "value": "936",
                          "label": "Sterling Mobile"
                      },
                      {
                          "value": "090305",
                          "label": "Sulsap MFB"
                      },
                      {
                          "value": "100",
                          "label": "Suntrust Bank Nigeria"
                      },
                      {
                          "value": "937",
                          "label": "TAGPAY"
                      },
                      {
                          "value": "302",
                          "label": "Taj Bank"
                      },
                      {
                          "value": "567",
                          "label": "TCF Micro-finance Bank"
                      },
                      {
                          "value": "924",
                          "label": "Teasy MOBILE"
                      },
                      {
                          "value": "102",
                          "label": "Titan Trust Bank"
                      },
                      {
                          "value": "100039",
                          "label": "TITAN-PAYSTACK MICROFINANCE BANK"
                      },
                      {
                          "value": "090146",
                          "label": "Trident Microfinance Bank"
                      },
                      {
                          "value": "090327",
                          "label": "Trust MFB"
                      },
                      {
                          "value": "090005",
                          "label": "Trustbond Mortgage Bank"
                      },
                      {
                          "value": "090276",
                          "label": "Trustfund Microfinance Bank"
                      },
                      {
                          "value": "090315",
                          "label": "U AND C MFB"
                      },
                      {
                          "value": "090331",
                          "label": "UNAAB MFB"
                      },
                      {
                          "value": "090266",
                          "label": "Uniben Microfinance Bank"
                      },
                      {
                          "value": "090193",
                          "label": "Unical Microfinance Bank"
                      },
                      {
                          "value": "032",
                          "label": "Union Bank"
                      },
                      {
                          "value": "033",
                          "label": "United Bank for Africa"
                      },
                      {
                          "value": "215",
                          "label": "Unity Bank"
                      },
                      {
                          "value": "090251",
                          "label": "UNN MFB"
                      },
                      {
                          "value": "090123",
                          "label": "Verite Microfinance Bank"
                      },
                      {
                          "value": "566",
                          "label": "VFD Micro-finance Bank"
                      },
                      {
                          "value": "090150",
                          "label": "Virtue Microfinance Bank"
                      },
                      {
                          "value": "090139",
                          "label": "Visa Microfinance Bank"
                      },
                      {
                          "value": "926",
                          "label": "VT Networks"
                      },
                      {
                          "value": "035",
                          "label": "Wema Bank"
                      },
                      {
                          "value": "954",
                          "label": "WetLand Micro-finance Bank"
                      },
                      {
                          "value": "090124",
                          "label": "Xslnce Microfinance Bank"
                      },
                      {
                          "value": "090142",
                          "label": "Yes Microfinance Bank"
                      },
                      {
                          "value": "057",
                          "label": "Zenith Bank"
                      },
                      {
                          "value": "322",
                          "label": "ZENITH Mobile"
                      },
                      {
                          "value": "939",
                          "label": "ZINTERNET - KONGAPAY"
                      },
                      {
                          "value": "090629",
                          "label": "9japay Microfinance Bank"
                      },
                      {
                          "value": "090672",
                          "label": "BellBank MFB"
                      },
                      {
                          "value": "090615",
                          "label": "Beststar Microfinance Bank"
                      },
                      {
                          "value": "090753",
                          "label": "Bold MFB"
                      },
                      {
                          "value": "090360",
                          "label": "Cashconnect Microfinance Bank"
                      },
                      {
                          "value": "090380",
                          "label": "KREDI MONEY MICROFINANCE BANK"
                      },
                      {
                          "value": "100042",
                          "label": "Pocket App"
                      },
                      {
                          "value": "050739",
                          "label": "Prospa Capital Microfinance Bank"
                      }
                  ],
                  "BR": [
                      {
                          "value": "001",
                          "label": "Banco do Brasil"
                      },
                      {
                          "value": "033",
                          "label": "Santander Brasil"
                      },
                      {
                          "value": "104",
                          "label": "Caixa Economica Federal"
                      },
                      {
                          "value": "237",
                          "label": "Bradesco"
                      },
                      {
                          "value": "341",
                          "label": "Itau Unibanco"
                      },
                      {
                          "value": "260",
                          "label": "Nu Pagamentos (Nubank)"
                      },
                      {
                          "value": "077",
                          "label": "Banco Inter"
                      },
                      {
                          "value": "336",
                          "label": "Banco C6"
                      },
                      {
                          "value": "212",
                          "label": "Banco Original"
                      },
                      {
                          "value": "748",
                          "label": "Sicredi"
                      }
                  ]
              }
          },
          {
              "name": "Country",
              "kind": "text",
              "required": false,
              "hidden": true
          }
      ]
  },
  49: {
      "paymentMethodTypeId": 49,
      "type": "BankTransferBrazil",
      "fields": [
          {
              "name": "PIX Key",
              "kind": "text",
              "required": true,
              "hidden": false
          },
          {
              "name": "Country Code",
              "kind": "select",
              "required": true,
              "hidden": false,
              "options": [
                  {
                      "value": "BR",
                      "label": "Brazil"
                  }
              ],
              "source": "profile"
          },
          {
              "name": "PIX Key Type",
              "key": "code",
              "kind": "select",
              "required": true,
              "hidden": false,
              "dependsOn": "Country Code",
              "options": {
                  "BR": [
                      {
                          "value": "CPF",
                          "label": "CPF"
                      },
                      {
                          "value": "CNPJ",
                          "label": "CNPJ"
                      },
                      {
                          "value": "PHONE",
                          "label": "PHONE"
                      },
                      {
                          "value": "EMAIL",
                          "label": "EMAIL"
                      },
                      {
                          "value": "EVP",
                          "label": "EVP"
                      }
                  ]
              }
          },
          {
              "name": "Country",
              "kind": "text",
              "required": false,
              "hidden": true
          }
      ]
  },
  41: {
      "paymentMethodTypeId": 41,
      "type": "Swift",
      "fields": [
          {
              "name": "Account opening bank",
              "kind": "text",
              "required": true,
              "hidden": false
          },
          {
              "name": "Account opening number",
              "kind": "text",
              "required": true,
              "hidden": false
          },
          {
              "name": "Account opening BIC",
              "kind": "text",
              "required": true,
              "hidden": false
          },
          {
              "name": "Account opening country",
              "kind": "text",
              "required": true,
              "hidden": false
          }
      ]
  },
};

/** Every schema the demo can reach, keyed the way the gateway keys them. */
export const REQUIREMENTS: Record<number, CxmPayoutAccountRequirements> = {
  ...TEXT_REQUIREMENTS,
  ...COMPLEX_REQUIREMENTS,
};
